import { DashboardTableSkeleton } from "./DashboardSkeleton";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { WEB_API_DASHBOARD } from "../../env";
import { encodeBase64 } from "../../utils/convertBase64";

const PAGE_SIZE = 20;
const thaiCollator = new Intl.Collator("th", {
    sensitivity: "base",
    numeric: true,
});

const formatNumber = (value) => {
    const number = Number(value);
    return Number.isNaN(number) ? "0" : number.toLocaleString("en-US");
};

const SurveyDetailTable = ({ filter, onClose }) => {
    const sectionRef = useRef(null);
    const [rows, setRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setCurrentPage(1);
        window.requestAnimationFrame(() => {
            sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }, [filter]);

    useEffect(() => {
        if (!filter) return undefined;

        const controller = new AbortController();
        const fetchRows = async () => {
            setLoading(true);
            setError("");
            try {
                const response = await fetch(
                    `${WEB_API_DASHBOARD}/dga-user-service-survey/dashboard/answer-dashboard`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(filter),
                        signal: controller.signal,
                    }
                );
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                const detailRows = Array.isArray(data?.agencyListeningRows)
                    ? data.agencyListeningRows
                    : [];
                setRows(detailRows.map((row, index) => ({
                    key: `${row.survey_id || "survey"}-${index}`,
                    month: String(row.month_th || "-").trim(),
                    monthOrder: Number(row.month_no) || 13,
                    agency: String(row.agency_name || "-").trim(),
                    title: String(row.survey_name || "-").trim(),
                    lawType: String(row.law_hierarchy_name || "ไม่ระบุ").trim(),
                    commentCount: Number(row.answer_per_question) || 0,
                    surveyId: row.survey_id,
                })).sort((left, right) => {
                    const monthDiff = left.monthOrder - right.monthOrder;
                    if (monthDiff !== 0) return monthDiff;

                    const agencyDiff = thaiCollator.compare(left.agency, right.agency);
                    if (agencyDiff !== 0) return agencyDiff;

                    return thaiCollator.compare(left.title, right.title);
                }));
            } catch (fetchError) {
                if (fetchError.name !== "AbortError") {
                    setRows([]);
                    setError("ไม่สามารถโหลดข้อมูลการรับฟังได้");
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };

        fetchRows();
        return () => controller.abort();
    }, [filter]);

    const totalPages = useMemo(
        () => Math.max(Math.ceil(rows.length / PAGE_SIZE), 1),
        [rows.length]
    );
    const pagedRows = useMemo(() => {
        const offset = (currentPage - 1) * PAGE_SIZE;
        return rows.slice(offset, offset + PAGE_SIZE);
    }, [currentPage, rows]);
    const firstItem = rows.length ? ((currentPage - 1) * PAGE_SIZE) + 1 : 0;
    const lastItem = Math.min(currentPage * PAGE_SIZE, rows.length);
    const selectedMonthText = filter?.month_label
        ? ` เดือน${filter.month_label}`
        : " ทุกเดือน (มกราคม-ธันวาคม)";

    return (
        <section ref={sectionRef} className="scroll-mt-24 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h2 className="text-base font-bold text-primary">รายละเอียดการรับฟัง</h2>
                    <p className="mt-1 text-xs text-slate-500">
                        {filter?.agency_name ? `${filter.agency_name} · ` : ""}{filter?.law_hierarchy_name || "ทุกศักดิ์กฎหมาย"}
                        {selectedMonthText}
                        {filter?.range_label ? ` ช่วงวันที่ ${filter.range_label}` : ""}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <p className="text-sm text-slate-600">
                        การรับฟัง <span className="text-lg font-bold text-slate-900">{formatNumber(rows.length)}</span> รายการ
                    </p>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50"
                    >
                        ปิดรายละเอียด
                    </button>
                </div>
            </div>

            <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full min-w-[1050px] text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-700">
                        <tr>
                            <th className="w-[130px] px-5 py-4">เดือน</th>
                            <th className="w-[230px] px-5 py-4">หน่วยงานที่เปิดรับฟัง</th>
                            <th className="px-5 py-4">หัวข้อการรับฟังความคิดเห็น</th>
                            <th className="w-[200px] px-5 py-4">ศักดิ์กฎหมาย</th>
                            <th className="w-[160px] px-5 py-4 text-center">ความคิดเห็น (ครั้ง)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <DashboardTableSkeleton columns={5} />
                        ) : error ? (
                            <tr>
                                <td colSpan="5" className="px-5 py-12 text-center text-red-500">{error}</td>
                            </tr>
                        ) : pagedRows.length ? pagedRows.map((row) => (
                            <tr key={row.key} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="px-5 py-4 text-slate-500">{row.month}</td>
                                <td className="px-5 py-4 text-slate-700">{row.agency}</td>
                                <td className="px-5 py-4">
                                    {row.surveyId ? (
                                        <Link
                                            to={`/listeningDetail?survey_id=${encodeBase64(row.surveyId)}`}
                                            className="font-medium text-sky-600 hover:text-sky-800 hover:underline"
                                        >
                                            {row.title}
                                        </Link>
                                    ) : row.title}
                                </td>
                                <td className="px-5 py-4 text-slate-700">{row.lawType}</td>
                                <td className="px-5 py-4 text-center font-semibold text-slate-900">
                                    {formatNumber(row.commentCount)}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="5" className="px-5 py-12 text-center text-slate-400">ไม่พบข้อมูลการรับฟัง</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
                <span>แสดง {formatNumber(firstItem)}-{formatNumber(lastItem)} จาก {formatNumber(rows.length)} รายการ</span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        disabled={currentPage === 1 || loading}
                        onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        ก่อนหน้า
                    </button>
                    <span>หน้า {formatNumber(currentPage)} / {formatNumber(totalPages)}</span>
                    <button
                        type="button"
                        disabled={currentPage === totalPages || loading}
                        onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        ถัดไป
                    </button>
                </div>
            </div>
        </section>
    );
};

export default SurveyDetailTable;
